import { createFileRoute, notFound, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Calendar, Check, Eye, MessageSquare, Star, UserCircle, X } from 'lucide-react';
import { toast } from 'sonner';
import Markdown from 'react-markdown';
import { useState } from 'react';
import type { PersonaReview } from '@/api/ideas/get-persona-reviews';
import type { IdeaStatus } from '@/api/ideas/type.ts';
import { useGetPersonaReviews } from '@/api/ideas/get-persona-reviews';
import { useGetIdea } from '@/api/ideas/get-idea';
import { useGetFeedback } from '@/api/feedbacks/get-feedback';
import { useUpdateIdeaStatus } from '@/api/ideas/update-idea-status';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FeedbackDetailModal } from '@/components/feedbacks/feedback-detail-modal';
import { getPriorityColor } from '@/utils/roadmap-utils';
import { useOrganization } from '@/contexts/organization-context';

type IdeaSearch = {
  from: 'roadmap' | 'ideas';
};

export const Route = createFileRoute('/_authenticated/roadmap/$id')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): IdeaSearch => {
    // validate and parse the search params into a typed state
    return {
      from: search.from === 'ideas' ? 'ideas' : 'roadmap'
    };
  }
});

const getPriorityLabel = (priority: string) => {
  switch (priority) {
    case 'High':
      return 'Haute';
    case 'Medium':
      return 'Moyenne';
    case 'Low':
      return 'Basse';
    default:
      return priority;
  }
};

// Composant pour afficher les étoiles de notation
const StarRating = ({ rating }: { rating: number }) => {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-4 w-4 ${
            star <= rating ? 'fill-yellow-400 text-yellow-400' : 'fill-none text-muted-foreground'
          }`}
        />
      ))}
    </div>
  );
};

function RouteComponent() {
  const { id } = Route.useParams();
  const { from: fromPage } = Route.useSearch();
  const navigate = useNavigate();
  const { organizationId, projectId } = useOrganization();
  const { data: idea, isLoading, isError } = useGetIdea(organizationId!, projectId!, id);
  const { data: personasData, isLoading: isLoadingPersonas } = useGetPersonaReviews(organizationId!, projectId!, id);
  const updateIdeaStatus = useUpdateIdeaStatus();

  // Modal state for feedback details
  const [selectedFeedbackId, setSelectedFeedbackId] = useState<string | null>(null);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

  // Fetch feedback details when a feedback is selected
  const { data: feedbackDetail, isLoading: isFeedbackLoading } = useGetFeedback(
    organizationId!,
    projectId!,
    selectedFeedbackId || '',
    isFeedbackModalOpen && !!selectedFeedbackId
  );

  const personas: Array<PersonaReview> = personasData?.reviews ?? [];

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleOpenFeedbackModal = (feedbackId: string) => {
    setSelectedFeedbackId(feedbackId);
    setIsFeedbackModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Chargement...</p>
      </div>
    );
  }

  if (isError || !idea) {
    throw notFound();
  }

  const priorityStyle = getPriorityColor(idea.priority);
  const PriorityIcon = priorityStyle.icon;

  const handleStatusChange = (newStatus: IdeaStatus) => {
    if (!organizationId || !projectId) return;
    updateIdeaStatus.mutate(
      {
        organizationId,
        projectId,
        ideaId: id,
        status: newStatus
      },
      {
        onSuccess: () => {
          if (newStatus === 'Done') {
            toast.success('Idée marquée comme réalisée');
          } else if (newStatus === 'Discarded') {
            toast.success('Idée mise de côté');
          }
        },
        onError: () => {
          toast.error("Impossible de mettre à jour le statut de l'idée");
        }
      }
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="pt-24 pb-20 px-4">
        <div className="container mx-auto max-w-5xl space-y-6">
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => navigate({ to: fromPage === 'ideas' ? '/ideas' : '/roadmap' })}
            className="mb-4 -ml-3"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour à {fromPage === 'ideas' ? 'mes idées' : 'la roadmap'}
          </Button>

          {/* Header Card */}
          <Card className="border-2 animate-fade-in">
            <CardHeader className="space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge
                      variant="outline"
                      className={`${priorityStyle.bg} ${priorityStyle.text} ${priorityStyle.border} font-semibold px-4 py-2 text-base rounded-full`}
                    >
                      <PriorityIcon className="h-5 w-5 mr-2" />
                      Priorité {getPriorityLabel(idea.priority)}
                    </Badge>
                    <Badge
                      variant="outline"
                      className={`${
                        idea.status === 'Pending'
                          ? 'bg-yellow-100 text-yellow-800 border-yellow-300'
                          : idea.status === 'Done'
                            ? 'bg-green-100 text-green-800 border-green-300'
                            : 'bg-red-100 text-red-800 border-red-300'
                      } font-semibold px-4 py-2 text-base rounded-full`}
                    >
                      {idea.status === 'Pending' ? 'En attente' : idea.status === 'Done' ? 'Réalisée' : 'Rejetée'}
                    </Badge>
                  </div>
                  <CardTitle className="text-2xl font-semibold leading-tight">{idea.title}</CardTitle>
                  {/* stats*/}
                  <div className="flex items-center gap-6 text-sm text-muted-foreground font-semibold leading-tight">
                    <div className={'flex items-center gap-1.5'}>
                      <MessageSquare className="h-4 w-4" />
                      {idea.feedbacks.length} feedback{idea.feedbacks.length > 1 ? 's' : ''}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Calendar className="h-4 w-4" />
                      Créée le {formatDate(idea.createdOn)}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Calendar className="h-4 w-4" />
                      Dernière mise à jour le {formatDate(idea.updatedOn)}
                    </div>
                  </div>
                </div>
                {idea.status === 'Pending' && (
                  <div className="flex gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1 text-green-600 hover:bg-white/50 hover:text-green-700"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleStatusChange('Done');
                      }}
                      disabled={updateIdeaStatus.isPending}
                    >
                      <Check className="h-4 w-4 rounded-full p-0.5 border border-green-600" />
                      Réalisée
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1  text-red-400 hover:bg-white/50 hover:text-red-500"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleStatusChange('Discarded');
                      }}
                      disabled={updateIdeaStatus.isPending}
                    >
                      <X className="h-4 w-4 rounded-full p-0.5 border border-red-400" />
                      Rejeter
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
          </Card>

          {/* Description Card */}
          <Card className="border-2 animate-fade-in" style={{ animationDelay: '0.1s' }}>
            <CardHeader>
              <CardTitle className="text-lg font-medium flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-purple-600" />
                Description consolidée
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-muted-foreground leading-relaxed whitespace-pre-line">
                <Markdown>{idea.description}</Markdown>
              </div>
            </CardContent>
          </Card>

          {/* Personas Section */}
          <Card className="border-2 animate-fade-in" style={{ animationDelay: '0.15s' }}>
            <CardHeader>
              <CardTitle className="text-lg font-medium flex items-center gap-2">
                <UserCircle className="h-5 w-5 text-purple-600" />
                Personas
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingPersonas && personas.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">Chargement des personas...</div>
              ) : personas.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Aucun persona n&apos;a encore évalué cette idée.
                </div>
              ) : (
                <div className="space-y-6">
                  {personas.map((persona, index) => (
                    <div key={persona.id} className="animate-fade-in" style={{ animationDelay: `${index * 0.05}s` }}>
                      <div className="flex gap-4">
                        {/* Avatar */}
                        <div className="shrink-0">
                          {persona.avatar ? (
                            <img
                              src={persona.avatar}
                              alt={persona.name}
                              className="h-12 w-12 rounded-full object-cover border-2 border-border"
                            />
                          ) : (
                            <div className="h-12 w-12 rounded-full border-2 border-border bg-muted flex items-center justify-center">
                              <UserCircle className="h-6 w-6 text-muted-foreground" />
                            </div>
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 space-y-2">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <h3 className="font-semibold text-base">{persona.name}</h3>
                              <p className="text-muted-foreground leading-relaxed mt-2">{persona.review}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <StarRating rating={persona.rating} />
                          </div>
                        </div>
                      </div>
                      {index < personas.length - 1 && <div className="mt-6 border-b" />}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Feedbacks Section */}
          <Card className="border-2 animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-medium flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-purple-600" />
                  Feedbacks
                </CardTitle>
                <span className="text-sm font-semibold">
                  {idea.feedbacks.length} feedback{idea.feedbacks.length > 1 ? 's' : ''}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {idea.feedbacks.map((atomicFeedback, index) => (
                  <div
                    key={atomicFeedback.id}
                    className="animate-fade-in"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <div className="space-y-3">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <p className="text-sm text-muted-foreground whitespace-pre-line">{atomicFeedback.content}</p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-2 shrink-0"
                          onClick={() => handleOpenFeedbackModal(atomicFeedback.feedbackId)}
                          aria-label="Voir les détails complets de ce feedback"
                        >
                          <Eye className="h-4 w-4" />
                          Voir le feedback complet
                        </Button>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="h-4 w-4" />
                          <span>{formatDate(atomicFeedback.createdOn)}</span>
                        </div>
                      </div>
                    </div>
                    {index < idea.feedbacks.length - 1 && <div className="mt-6 border-b" />}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Feedback Detail Modal */}
      <FeedbackDetailModal
        open={isFeedbackModalOpen}
        onOpenChange={setIsFeedbackModalOpen}
        feedbackDetail={feedbackDetail}
        isLoading={isFeedbackLoading}
        formatDate={formatDate}
      />
    </div>
  );
}
